import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Toolbar,
  Tooltip,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon,
  Grid,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Delete,
  PersonAdd,
  AdminPanelSettings,
  Block,
  CheckCircle,
  MoreVert,
  Download,
  Refresh,
  Person,
  WorkOutline,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';
import AddUserDialog, { UserFormData } from '../../components/Users/AddUserDialog';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  isAdmin: boolean;
  role: string; // Role from permissions system
  createdAt: string;
  lastLoginAt?: string;
  phone?: string;
  country?: string;
  totalBots: number;
  activeBots: number;
  totalTrades: number;
  totalVolume: number;
  subscriptionTier: 'free' | 'basic' | 'premium' | 'enterprise';
  emailVerified: boolean;
  phoneVerified: boolean;
}

interface UserFilters {
  search: string;
  status: 'all' | 'active' | 'inactive';
  admin: 'all' | 'admin' | 'user';
  subscription: 'all' | 'free' | 'basic' | 'premium' | 'enterprise';
  verified: 'all' | 'verified' | 'unverified';
}

const Users: React.FC = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // State management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    status: 'all',
    admin: 'all',
    subscription: 'all',
    verified: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [bulkActionAnchor, setBulkActionAnchor] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);

  // Fetch users data
  const { data: usersData, isLoading, error, refetch } = useQuery<any, Error>({
    queryKey: ['users', page, rowsPerPage, filters],
    queryFn: () => api.get(`/users?page=${page}&limit=${rowsPerPage}&${new URLSearchParams(filters as any).toString()}`),
    keepPreviousData: true,
  });

  // Fetch available roles for filter
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get('/roles'),
  });

  const availableRoles = rolesData?.roles || [];

  console.log("Users data:", usersData);

  const users = usersData?.users || [];
  const totalUsers = usersData?.total || 0;

  // Mutations for user actions
  const updateUserMutation = useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: Partial<User> }) =>
      api.put(`/users/${userId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update user', severity: 'error' });
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: ({ userIds, updates }: { userIds: string[]; updates: Partial<User> }) =>
      api.put('/users/bulk', { userIds, updates }),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSelected([]);
      setSnackbar({ open: true, message: 'Users updated successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to update users', severity: 'error' });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (userData: UserFormData) => api.post('/users', userData),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to create user', severity: 'error' });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (userIds: string[]) => api.post('/users/bulk/delete', { userIds }),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSelected([]);
      setSnackbar({ open: true, message: 'Users deleted successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to delete users', severity: 'error' });
    },
  });

  // Event handlers
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = users.map((user: User) => user.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (key: keyof UserFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleUserDetails = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleCreateUser = async (userData: UserFormData) => {
    await createUserMutation.mutateAsync(userData);
  };

  const handleBulkAction = (action: string) => {
    setBulkActionAnchor(null);

    switch (action) {
      case 'activate':
        bulkUpdateMutation.mutate({ userIds: selected, updates: { isActive: true } });
        break;
      case 'deactivate':
        bulkUpdateMutation.mutate({ userIds: selected, updates: { isActive: false } });
        break;
      case 'grant_admin':
        bulkUpdateMutation.mutate({ userIds: selected, updates: { isAdmin: true } });
        break;
      case 'revoke_admin':
        bulkUpdateMutation.mutate({ userIds: selected, updates: { isAdmin: false } });
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selected.length} user(s)? This action cannot be undone and will delete all related data.`)) {
          bulkDeleteMutation.mutate(selected);
        }
        break;
      case 'export':
        // TODO: Implement export functionality
        break;
    }
  };

  const getSubscriptionColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'default';
      case 'basic': return 'primary';
      case 'premium': return 'secondary';
      case 'enterprise': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, permissions, and bulk operations
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            size="small"
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            size="small"
            onClick={() => setAddUserDialogOpen(true)}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ mb: 2 }}>
        <Toolbar>
          <TextField
            placeholder="Search users..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300, mr: 2 }}
            size="small"
          />

          <Button
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? "contained" : "outlined"}
            size="small"
            sx={{ mr: 2 }}
          >
            Filters
          </Button>

          {selected.length > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {selected.length} selected
              </Typography>
              <Button
                startIcon={<MoreVert />}
                onClick={(e) => setBulkActionAnchor(e.currentTarget)}
                variant="outlined"
                size="small"
              >
                Bulk Actions
              </Button>
            </Box>
          )}
        </Toolbar>

        {showFilters && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={filters.admin}
                    label="Role"
                    onChange={(e) => handleFilterChange('admin', e.target.value)}
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    {availableRoles.map((role: any) => (
                      <MenuItem key={role.id} value={role.name.toLowerCase()}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Subscription</InputLabel>
                  <Select
                    value={filters.subscription}
                    label="Subscription"
                    onChange={(e) => handleFilterChange('subscription', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Verification</InputLabel>
                  <Select
                    value={filters.verified}
                    label="Verification"
                    onChange={(e) => handleFilterChange('verified', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="verified">Verified</MenuItem>
                    <MenuItem value="unverified">Unverified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selected.length > 0 && selected.length < users.length}
                    checked={users.length > 0 && selected.length === users.length}
                    onChange={handleSelectAllClick}
                    disabled={isLoading || !!error}
                  />
                </TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Bots</TableCell>
                <TableCell>Trades</TableCell>
                <TableCell>Volume</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <LoadingSpinner />
                  </TableCell>
                </TableRow>
              )}

              {error && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <Alert severity="error" sx={{ my: 2 }}>
                      <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                        Failed to load users
                      </Typography>
                      <Typography variant="body2">
                        {error?.message || 'An error occurred while fetching users. Please try again.'}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => refetch()}
                        sx={{ mt: 2 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !error && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !error && users.map((user: User) => {
                const isItemSelected = isSelected(user.id);
                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, user.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={user.id}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                        <Box display="flex" gap={0.5} mt={0.5}>
                          {user.emailVerified && (
                            <Chip label="Email" size="small" color="success" variant="outlined" />
                          )}
                          {user.phoneVerified && (
                            <Chip label="Phone" size="small" color="success" variant="outlined" />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role || (user.isAdmin ? 'Admin' : 'User')}
                        color={
                          user.role === 'Admin' ? 'secondary' :
                          user.role === 'Staff' ? 'warning' :
                          'default'
                        }
                        size="small"
                        icon={
                          user.role === 'Admin' ? <AdminPanelSettings /> :
                          user.role === 'Staff' ? <WorkOutline /> :
                          <Person />
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1)}
                        color={getSubscriptionColor(user.subscriptionTier) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.activeBots}/{user.totalBots}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        active/total
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.totalTrades.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        ${user.totalVolume.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleDateString()
                          : 'Never'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUserDetails(user.id);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? "Deactivate" : "Activate"}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateUserMutation.mutate({
                                userId: user.id,
                                updates: { isActive: !user.isActive }
                              });
                            }}
                          >
                            {user.isActive ? <Block /> : <CheckCircle />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to delete this user?')) {
                                deleteUserMutation.mutate(user.id);
                              }
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkActionAnchor}
        open={Boolean(bulkActionAnchor)}
        onClose={() => setBulkActionAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkAction('activate')}>
          <ListItemIcon>
            <CheckCircle fontSize="small" />
          </ListItemIcon>
          Activate Selected
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('deactivate')}>
          <ListItemIcon>
            <Block fontSize="small" />
          </ListItemIcon>
          Deactivate Selected
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('grant_admin')}>
          <ListItemIcon>
            <AdminPanelSettings fontSize="small" />
          </ListItemIcon>
          Grant Admin
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('revoke_admin')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Revoke Admin
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('delete')} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          Delete Selected
        </MenuItem>
        <MenuItem onClick={() => handleBulkAction('export')}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          Export Selected
        </MenuItem>
      </Menu>



      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add User Dialog */}
      <AddUserDialog
        open={addUserDialogOpen}
        onClose={() => setAddUserDialogOpen(false)}
        onSubmit={handleCreateUser}
      />
    </Box>
  );
};

export default Users;
